import React from 'react';
import { UserPlus, Search, ShieldCheck, KeyRound, ArrowRight, Lock } from 'lucide-react';

interface HowItWorksSectionProps {
  onStepClick?: (stepIndex: number) => void;
}

export const HowItWorksSection: React.FC<HowItWorksSectionProps> = ({ onStepClick }) => {
  const steps = [
    {
      step: 1,
      icon: UserPlus,
      title: 'Sign Up for Free',
      description: '100% free client registration with instant OTP verification',
      color: 'from-pink-500 to-rose-500',
      bgLight: 'bg-pink-50 text-pink-600 border-pink-200',
    },
    {
      step: 2,
      icon: Search,
      title: 'Choose Companion',
      description: 'Select verified partner for date, dinner or event',
      color: 'from-purple-500 to-pink-500',
      bgLight: 'bg-purple-50 text-purple-600 border-purple-200',
    },
    {
      step: 3,
      icon: ShieldCheck,
      title: 'Book & Safe Escrow',
      description: 'Pay safely to Escrow Vault & receive Secret Meet OTP',
      color: 'from-indigo-500 to-purple-600',
      bgLight: 'bg-indigo-50 text-indigo-600 border-indigo-200',
    },
    {
      step: 4,
      icon: KeyRound,
      title: 'Meet & Share OTP',
      description: 'Meet in person. Share OTP to release Companion payment & complete meeting',
      color: 'from-emerald-500 to-teal-600',
      bgLight: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    },
  ];

  return (
    <section id="how-it-works-section" className="py-16 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            How It Works <span className="text-pink-500 inline-block">💕</span>
          </h2>
          <p className="mt-3 text-slate-500 text-sm sm:text-base">
            Secure Escrow & Secret 4-Digit Meet OTP Verification System
          </p>
        </div>

        {/* Steps Grid with Connectors */}
        <div className="relative">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {steps.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.step}
                  id={`how-it-works-step-${item.step}`}
                  onClick={() => onStepClick && onStepClick(item.step)}
                  className="relative group flex flex-col items-center text-center p-6 rounded-2xl bg-white hover:bg-slate-50/70 border border-slate-100 hover:border-pink-200 transition-all duration-300 shadow-xs hover:shadow-md cursor-pointer"
                >
                  {/* Step icon pill with number */}
                  <div className="relative mb-5 flex items-center justify-center">
                    {/* Number Badge */}
                    <div className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-pink-100 text-pink-600 font-extrabold text-xs flex items-center justify-center border-2 border-white shadow-xs z-10">
                      {item.step}
                    </div>

                    {/* Main Icon Circle */}
                    <div className={`w-20 h-20 rounded-2xl ${item.bgLight} border flex items-center justify-center group-hover:scale-110 group-hover:rotate-2 transition-transform duration-300 shadow-xs`}>
                      <Icon className="w-8 h-8" />
                    </div>
                  </div>

                  {/* Text Details */}
                  <h3 className="text-lg font-bold text-slate-900 group-hover:text-pink-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-xs sm:text-sm text-slate-500 leading-relaxed max-w-[200px]">
                    {item.description}
                  </p>

                  {/* Dashed connector arrow for large screens */}
                  {idx < steps.length - 1 && (
                    <div className="hidden lg:flex items-center absolute -right-3 top-1/3 transform -translate-y-1/2 z-20 text-pink-200">
                      <span className="border-b-2 border-dashed border-pink-200 w-6 mr-1" />
                      <ArrowRight className="w-4 h-4 text-pink-300" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Highlight Banner */}
        <div className="mt-12 p-5 bg-gradient-to-r from-pink-50 via-purple-50 to-pink-50 rounded-2xl border border-pink-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-pink-600 text-white flex items-center justify-center shrink-0 shadow-xs">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-slate-900">
                100% Escrow & Meet OTP Protection Guarantee
              </h4>
              <p className="text-xs text-slate-600">
                Payment is only released to the companion once the client shares their secret OTP upon meetup completion. 100% risk-free and fraud protected!
              </p>
            </div>
          </div>
          <span className="px-3 py-1.5 bg-white text-pink-700 font-extrabold text-xs rounded-xl border border-pink-200 shadow-xs shrink-0">
            ✓ Zero Fraud Guarantee
          </span>
        </div>

        {/* Companion Earning Callout Card */}
        <div className="mt-6 p-5 sm:p-6 bg-gradient-to-r from-purple-950 via-slate-900 to-purple-900 rounded-2xl border border-purple-800 text-white flex flex-col md:flex-row items-center justify-between gap-4 shadow-lg">
          <div className="flex items-center gap-4 text-center sm:text-left">
            <div className="w-12 h-12 rounded-2xl bg-purple-600/30 border border-purple-400/40 text-purple-300 flex items-center justify-center shrink-0 text-xl font-bold">
              ✨
            </div>
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-purple-500/20 border border-purple-400/30 text-purple-200 text-[11px] font-bold mb-1">
                <span>Companion Opportunities</span>
              </div>
              <h3 className="text-base sm:text-lg font-black text-white">
                Become a Companion • Earn Up to ₹1 Lakh
              </h3>
              <p className="text-xs text-slate-300 max-w-xl mt-0.5">
                Join verified companions across top cities. Set your own pricing, decide your availability, and enjoy guaranteed digital payouts after every meetup.
              </p>
            </div>
          </div>

          <a
            href="/companion/register"
            className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold text-xs sm:text-sm rounded-xl shadow-md shadow-pink-500/20 hover:scale-103 transition-all cursor-pointer shrink-0 flex items-center gap-2"
          >
            <span>Register as Companion</span>
            <span>→</span>
          </a>
        </div>

      </div>
    </section>
  );
};

